<script setup lang="ts">
import type { SelectOption } from '@camelot/components'
import { CtAutoComplete } from '@camelot/components'
import * as Sentry from '@sentry/vue'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { useVirtualStations } from '~/composables/api/useVirtualStations'
import { CITIES_RECORD } from '~/composables/city'
import { useGoogleAPI } from '~/composables/google-api'
import { translate, useTranslations } from '~/composables/translate/translations'
import { groupVirtualStations } from '~/composables/virtual-station-mapper'
import { useLocationStore } from '~/stores/location'
import type { Address, GroupedSelectOption } from '~/types/location'

type AutocompletePrediction = google.maps.places.AutocompletePrediction
type AutocompleteResponse = google.maps.places.AutocompleteResponse
type AutocompletionRequest = google.maps.places.AutocompletionRequest

const { autoCompleteService } = useGoogleAPI()

const locationStore = useLocationStore()
const {
  city,
  addressOptions,
  address,
  addressError,
  lastTypedQuery,
} = storeToRefs(locationStore)

const { language } = useTranslations()

const cityId = computed(() => city.value.id)
const virtualStations = useVirtualStations(cityId)

const groupedVirtualStations = computed<GroupedSelectOption[]>(() => {
  if (!virtualStations.value || virtualStations.value.length === 0) {
    return []
  }
  return groupVirtualStations(virtualStations.value)
})

// Convert the Set to an Array for the CtAutoComplete component.
// IMPORTANT: This is used with :filtering-fn to bypass the component's internal filtering.
// Without this, abbreviated street names (e.g., "ifflandstr") won't show full results ("Ifflandstraße").
const addressOptionsArray = computed(() => {
  const optionsArray = Array.from(addressOptions.value)

  // Separate grouped options from flat options
  const grouped: GroupedSelectOption[] = []
  const flat: SelectOption[] = []

  optionsArray.forEach((option) => {
    if ('options' in option && Array.isArray(option.options)) {
      grouped.push(option as GroupedSelectOption)
    } else {
      flat.push(option as SelectOption)
    }
  })

  // Return grouped first, then flat
  return [...grouped, ...flat] as any[]
})

watch(city, (_) => {
  useRouter().replace({ params: { _city: city.value.id } })
  addressOptions.value.clear()
  address.value = undefined
  lastTypedQuery.value = ''
})

watch(language, () => {
  const hasGroupedOptions = Array.from(addressOptions.value).some(opt => 'options' in opt)
  if (hasGroupedOptions) {
    addressOptions.value.clear()
    groupedVirtualStations.value.forEach(group => addressOptions.value.add(group as any))
  }
})

function buildRequest(query: string): AutocompletionRequest {
  const normalizedQuery = query.trim()

  return {
    language: language.value,
    input: normalizedQuery,
    locationBias: new google.maps.Circle({
      radius: city.value.zoneArea.radius,
      center: {
        lat: city.value.zoneArea.center.latitude,
        lng: city.value.zoneArea.center.longitude,
      },
    }),
    componentRestrictions: { country: city.value.country },
    types: ['address'],
  }
}

async function predictAddress(query?: string) {
  lastTypedQuery.value = query ?? ''
  addressOptions.value.clear()

  // If no query, show only virtual stations (grouped)
  if (!query) {
    groupedVirtualStations.value.forEach((group) => {
      addressOptions.value.add(group as any) // Type assertion needed for Set compatibility
    })
    return
  }

  // If user is typing, show ONLY Google Places results (no virtual stations)
  if (!autoCompleteService.value) {
    // If Google service not available and user is typing, show nothing
    return
  }

  try {
    const request = buildRequest(query)
    const response: AutocompleteResponse = await autoCompleteService.value.getPlacePredictions(request)

    if (response && response.predictions) {
      response.predictions.forEach((pred: AutocompletePrediction) => {
        const value: Address = { info: pred.description }
        addressOptions.value.add({
          label: pred.description,
          value,
        })
      })
    }
  } catch (error) {
    Sentry.captureMessage(
      'Error while fetching predictions',
      {
        level: 'error',
        extra: {
          input: query,
          city: city.value,
        },
        tags: { operation: 'predict-address' },
      },
    )
    console.error('Error while fetching predictions', error)
  }
}

function onFocusIn() {
  if (addressOptions.value.size === 0) {
    predictAddress()
  }
}

// Function to detect city from address text and update city store
function detectAndUpdateCityFromAddress(addressText: string) {
  if (!addressText) {
    return
  }

  const addressLower = addressText.toLowerCase()

  for (const [cityKey, cityObj] of Object.entries(CITIES_RECORD)) {
    if (addressLower.includes(cityKey) || addressLower.includes(cityObj.name.toLowerCase())) {
      if (city.value.id !== cityKey) {
        locationStore.city = cityObj
      }
      break
    }
  }
}

// Watch for address changes to automatically update city
watch(address, (newAddress) => {
  if (newAddress?.info) {
    detectAndUpdateCityFromAddress(newAddress.info)
  }
})
</script>

<template>
  <CtAutoComplete
    v-model="address" :label="translate('field_label_address_app')" :options="addressOptionsArray"
    :messages="addressError" :error="!!addressError" :placeholder="translate('placeholder_address')" :clearable="true"

    :filtering-fn="() => addressOptionsArray"
    prefix-icon="location" @update:query="predictAddress"
    @focusin="onFocusIn"
  />
</template>
